import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { FileDown, Upload, Plus } from "lucide-react";
import { gov, downloadFile } from "@/lib/governanceApi";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { card, label, input, Pill, SectionTitle, Act, Field, errMsg } from "./ui";

const AGR_FIELDS = ["customer_legal_name", "customer_address", "effective_date", "expiry_date", "signatory_name", "signatory_title", "signatory_email"];

const NewAgreement = ({ team, open, onClose, onDone }) => {
  const [f, setF] = useState({ type: "dpa", customer_legal_name: team?.name || "" });
  const go = async () => {
    try { const a = await gov.createAgreement({ ...f, team_id: team.id }); toast.success("Draft agreement generated"); onDone(a.id); onClose(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="new-agreement-dialog" className="max-h-[90vh] overflow-auto">
        <DialogHeader><DialogTitle>New agreement — {team?.name}</DialogTitle></DialogHeader>
        <Field name="type" spec={{ enum: ["dpa", "msa"] }} value={f.type} testid="agr-type" onChange={(n, v) => setF({ ...f, [n]: v })} />
        {AGR_FIELDS.map((k) => <Field key={k} name={k} spec={k.endsWith("_date") ? { type: "date" } : {}} value={f[k]} testid={`agr-${k}`} onChange={(n, v) => setF({ ...f, [n]: v })} />)}
        <p className="text-xs text-zinc-500">Generated from the latest template version. It can only be issued once that template is approved.</p>
        <Act onClick={go} data-testid="agr-create">Generate draft</Act>
      </DialogContent>
    </Dialog>
  );
};

const AgreementPanel = ({ id, onChanged }) => {
  const qc = useQueryClient();
  const { data: a } = useQuery({ queryKey: ["gov-agr", id], queryFn: () => gov.agreement(id) });
  const [signed, setSigned] = useState("");
  const [busy, setBusy] = useState(false);
  if (!a) return null;
  const refresh = () => { qc.invalidateQueries({ queryKey: ["gov-agr", id] }); onChanged(); };
  const run = async (fn, msg) => { setBusy(true); try { await fn(); toast.success(msg); refresh(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  const t = (action, extra = {}) => run(() => gov.agrTransition(id, { action, ...extra }), `Done: ${action}`);
  return (
    <div className={`${card} p-4 space-y-3`} data-testid="agreement-panel">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="font-display text-lg font-extrabold">{a.type.toUpperCase()} · {a.customer_legal_name}</h3>
        <Pill value={a.status} testid="agr-status" />
        <span className="text-xs text-zinc-500">template v{a.template_version} ({a.template_status})</span>
        {a.integrity_ok === false && <Pill value="critical" testid="agr-integrity-fail" />}
      </div>
      <div className="text-xs text-zinc-600 grid md:grid-cols-2 gap-1">
        <div>Signatory: {a.signatory_name || "—"} {a.signatory_title && `(${a.signatory_title})`}</div>
        <div>Effective: {a.effective_date || "—"} · Expiry: {a.expiry_date || "—"}</div>
        <div>Signed: {a.signed_date || "—"}</div>
        {a.content_hash && <div className="font-mono break-all">SHA-256: {a.content_hash}</div>}
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        {a.status === "draft" && <>
          <Act onClick={() => t("issue")} disabled={busy} data-testid="agr-issue">Issue (freeze)</Act>
          <Act tone="ghost" onClick={() => run(() => gov.saveAgreement(id, {}), "Re-rendered from latest template")} data-testid="agr-refresh">Refresh from template</Act>
          <Act tone="danger" onClick={() => t("void")} data-testid="agr-void">Void</Act>
        </>}
        {["issued", "signed"].includes(a.status) && (
          <label className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider border-2 border-[#14304F] px-3 py-1.5 cursor-pointer hover:bg-zinc-50">
            <Upload size={13} /> Upload signed copy
            <input type="file" hidden data-testid="agr-evidence-input" accept=".pdf,.docx,.png,.jpg,.jpeg" onChange={(e) => e.target.files[0] && run(() => gov.uploadEvidence(id, e.target.files[0]), "Evidence uploaded")} />
          </label>
        )}
        {a.status === "issued" && <>
          <input type="date" data-testid="agr-signed-date" className={`${input} w-40`} value={signed} onChange={(e) => setSigned(e.target.value)} />
          <Act onClick={() => t("mark_signed", { signed_date: signed })} disabled={busy} data-testid="agr-mark-signed">Mark signed</Act>
        </>}
        {["issued", "signed"].includes(a.status) && <Act tone="danger" onClick={() => t("terminate")} data-testid="agr-terminate">Terminate</Act>}
        <Act tone="ghost" onClick={() => downloadFile(`/agreements/${id}/pdf`, `${a.type}-${id.slice(0, 8)}.pdf`)} data-testid="agr-pdf"><FileDown size={14} className="mr-1" /> PDF</Act>
      </div>
      {a.evidence?.length > 0 && (
        <div data-testid="agr-evidence-list">
          <div className={label}>Signature evidence</div>
          {a.evidence.map((e) => (
            <button key={e.id} onClick={() => downloadFile(`/agreements/${id}/evidence/${e.id}`, e.filename)} className="block text-xs text-[#2F6DA3] hover:underline font-mono">
              {e.filename} · {e.size < 1024 ? `${e.size} B` : `${(e.size / 1024).toFixed(0)} KB`} · {e.uploaded_at.slice(0, 10)} · sha256 {e.sha256.slice(0, 12)}…
            </button>
          ))}
        </div>
      )}
      <pre className="whitespace-pre-wrap text-xs bg-zinc-50 border border-zinc-200 p-3 max-h-[45vh] overflow-auto font-mono">{a.content_md}</pre>
    </div>
  );
};

export default function Agreements() {
  const qc = useQueryClient();
  const { data: orgs = [] } = useQuery({ queryKey: ["gov-orgs"], queryFn: gov.organisations });
  const [newFor, setNewFor] = useState(null);
  const [sel, setSel] = useState(null);
  const changed = () => qc.invalidateQueries({ queryKey: ["gov-orgs"] });
  return (
    <div className="space-y-4">
      <SectionTitle sub="Per-organisation compliance profile. An agreement can only be marked signed when a signed copy is uploaded and the signatory and date are recorded.">Organisations & agreements</SectionTitle>
      {orgs.map((o) => (
        <div key={o.id} className={`${card} p-4`} data-testid={`org-${o.id}`}>
          <div className="flex flex-wrap items-center gap-2 justify-between">
            <div className="flex items-center gap-2">
              <span className="font-display font-extrabold">{o.name}</span>
              <Pill value={o.authorised ? "authorised" : "not authorised"} />
              <span className="text-xs text-zinc-500">{o.members} member(s) · owner {o.owner?.email}</span>
            </div>
            <Act tone="ghost" onClick={() => setNewFor(o)} data-testid={`org-new-agreement-${o.id}`}><Plus size={13} className="mr-1" /> New agreement</Act>
          </div>
          {o.authorised && o.missing.length > 0 && <div className="text-xs text-red-700 mt-2" data-testid="org-missing">Missing signed: {o.missing.join(", ").toUpperCase()}</div>}
          <div className="mt-2 flex flex-wrap gap-2">
            {o.agreements.map((a) => (
              <button key={a.id} data-testid={`agr-${a.id}`} onClick={() => setSel(a.id)} className={`text-xs border px-2 py-1 flex items-center gap-2 ${sel === a.id ? "border-[#2F6DA3] bg-sky-50" : "border-zinc-200"}`}>
                {a.type.toUpperCase()} <Pill value={a.status} /> {a.created_at.slice(0, 10)}
              </button>
            ))}
          </div>
        </div>
      ))}
      {sel && <AgreementPanel key={sel} id={sel} onChanged={changed} />}
      {newFor && <NewAgreement team={newFor} open onClose={() => setNewFor(null)} onDone={(id) => { changed(); setSel(id); }} />}
    </div>
  );
}
